import type { RefObject } from "react";
import { Alert, Button, Card, Radio, Skeleton, TextField, Textarea } from "@/components/ui";
import type { PublicPlatform } from "@/lib/api/types";
import { SPONSORED_TAIL } from "@/lib/copy";
import { charCount } from "@/lib/platform";
import type { Gen } from "../store";

/** Step 3: pick one of three drafts, then edit it (title too when the platform has one). */
export function StepDrafts({
  gen,
  choice,
  text,
  title,
  primary,
  sponsored,
  name,
  textRef,
  demo,
  onPick,
  onText,
  onTitle,
  onRetry,
  onWriteSelf,
}: {
  gen: Gen;
  choice: number;
  text: string;
  title: string;
  primary: PublicPlatform | undefined;
  sponsored: boolean;
  name: string;
  textRef: RefObject<HTMLTextAreaElement | null>;
  demo: boolean;
  onPick: (i: number) => void;
  onText: (t: string) => void;
  onTitle: (t: string) => void;
  onRetry: () => void;
  onWriteSelf: () => void;
}) {
  if (gen.kind === "idle" || gen.kind === "loading") {
    return (
      <div className="mt-4 flex flex-col gap-3">
        <output className="block text-label-neutral">
          초안을 쓰고 있어요. 보통 5초 안팎이에요.
        </output>
        {[0, 1, 2].map((i) => (
          <Card key={i} padding={16} radius="var(--radius-md)">
            <Skeleton width="30%" height={16} />
            <Skeleton width="100%" height={14} style={{ marginTop: 12 }} />
            <Skeleton width="72%" height={14} style={{ marginTop: 8 }} />
          </Card>
        ))}
      </div>
    );
  }

  if (gen.kind === "error") {
    return (
      <Card padding={20} radius="var(--radius-lg)" style={{ marginTop: 16 }} role="alert">
        <p className="font-semibold text-label-normal">{gen.message}</p>
        {gen.waitSeconds !== null ? (
          <p className="mt-2 tabular-nums text-label-neutral">
            {gen.waitSeconds}초 뒤에 다시 만들어 드릴게요. 기다려 주셔서 고마워요.
          </p>
        ) : (
          <div className="mt-4 flex flex-wrap gap-2">
            <Button variant="outlined" color="assistive" size="large" onClick={onRetry}>
              다시 시도하기
            </Button>
            <Button variant="text" color="assistive" size="large" onClick={onWriteSelf}>
              직접 써서 남기기
            </Button>
          </div>
        )}
      </Card>
    );
  }

  const hasTitle = !!primary?.has_title;
  const bodyMax = primary?.body_max ?? null;
  const count = charCount(text);
  const over = bodyMax !== null && count > bodyMax;

  return (
    <div className="mt-4 flex flex-col gap-6">
      {gen.kind === "manual" ? (
        <Alert tone={gen.reason === "preview" ? "info" : "cautionary"}>
          {gen.reason === "quota"
            ? (gen.message ??
              "오늘은 많이 찾아 주셔서 초안을 더 만들 수 없어요. 직접 한두 문장만 적어 주셔도 정말 고마워요.")
            : gen.reason === "preview"
              ? "미리보기에서는 초안을 만들지 않아요. 초안 생성은 월 한도에 들어가서, 고객이 실제 링크에서 누를 때만 만들어요."
              : "떠오르는 대로 한두 문장만 적어 주셔도 충분해요."}
        </Alert>
      ) : (
        <fieldset>
          <legend className="text-label-neutral">
            하나를 고른 뒤 아래 칸에서 내 말로 고쳐 주세요.
            {demo ? " 체험용 예시 초안이에요." : ""}
          </legend>
          <div className="mt-3 flex flex-col gap-2">
            {gen.drafts.map((d, i) => (
              <Radio
                key={`${i}-${d.text}`}
                variant="row"
                name={`${name}-draft`}
                checked={choice === i}
                onChange={() => onPick(i)}
                label={d.title ? `초안 ${i + 1}: ${d.title}` : `초안 ${i + 1}`}
                description={d.text}
              />
            ))}
          </div>
          <div className="mt-2 flex justify-end">
            <Button variant="text" size="large" onClick={onRetry}>
              다른 초안 보기
            </Button>
          </div>
        </fieldset>
      )}

      {hasTitle ? (
        <TextField
          label="제목"
          value={title}
          onChange={(e) => onTitle(e.target.value)}
          helperText={`${primary?.name}처럼 제목 칸이 있는 곳에 따로 붙여 넣어요.`}
          style={{ fontSize: "inherit" }}
        />
      ) : null}

      <Textarea
        ref={textRef}
        label="올릴 후기"
        value={text}
        rows={6}
        fontSize="var(--font-size-headline-2)"
        onChange={(e) => onText(e.target.value)}
        error={over}
        helperText={
          over && primary
            ? `${primary.name}에는 ${bodyMax}자까지 올라가요. ${count - (bodyMax ?? 0)}자를 줄여 주세요.`
            : "있었던 그대로 고쳐 쓰셔도 좋아요."
        }
        counter={bodyMax ? `${count} / ${bodyMax}자` : `${count}자`}
      />

      {sponsored ? (
        <Alert tone="cautionary" title="마지막 줄은 지우지 말아 주세요">
          제품을 제공받고 쓰는 후기라서 "{SPONSORED_TAIL}" 문장을 꼭 남겨야 해요. 지우셔도 복사할 때
          다시 붙여 드려요.
        </Alert>
      ) : null}
    </div>
  );
}
